import { createContext, useContext, useEffect, useMemo, useState } from "react";
import en from "./en.json";
import ch from "./ch.json";
import my from "./my.json";
import { useSearchParams } from "react-router-dom";
import ApiService from "../../components/Api_services";
import {
  getAfterSalesApiSerial,
  getAfterSalesSerialType,
} from "./serialTypeUtils";

const LANGUAGES = { en, ch, my };

const PRODUCT_NAME_MAP = {
  "Air Conditioner": {
    en: "Air Conditioner",
    ch: "空调",
    my: "Penghawa Dingin",
  },
  "Water Heater": {
    en: "Water Heater",
    ch: "热水器",
    my: "Pemanas Air",
  },
};

const localizeProductName = (productName, lang) => {
  const mapped = PRODUCT_NAME_MAP[productName];
  if (!mapped) return productName;
  return mapped[lang] || productName;
};

const applyProductName = (dict, productName) => {
  if (!productName) return dict;

  return Object.fromEntries(
    Object.entries(dict).map(([key, value]) => [
      key,
      typeof value === "string"
        ? value.replaceAll("{product_name}", productName)
        : value,
    ]),
  );
};

const LanguageContext = createContext({
  trans: en,
  lang: "en",
  setLanguage: () => {},
  mainItemCode: "",
  countryCode: "",
  itemId1: null,
  itemId2: null,
  warrantyLoaded: false,
});

export const LanguageProvider = ({ children }) => {
  const [lang, setLang] = useState(localStorage.getItem("afs-lang") || "en");
  const [productName, setProductName] = useState("");
  const [mainItemCode, setMainItemCode] = useState("");
  const [countryCode, setCountryCode] = useState("");
  const [itemId1, setItemId1] = useState(null);
  const [itemId2, setItemId2] = useState(null);
  // true once warranty info for the current serial has been fetched (or there is no serial)
  const [warrantyLoaded, setWarrantyLoaded] = useState(false);

  const [searchParams] = useSearchParams();
  const serialNoForApi = getAfterSalesApiSerial(searchParams);
  const serialType = getAfterSalesSerialType(searchParams);

  const setLanguage = (code) => {
    setLang(code);
    localStorage.setItem("afs-lang", code);
  };

  useEffect(() => {
    let cancelled = false;

    const fetchWarrantyData = async () => {
      setMainItemCode("");
      setCountryCode("");
      setItemId1(null);
      setItemId2(null);
      setProductName("");

      if (!serialNoForApi) {
        setWarrantyLoaded(true);
        return;
      }

      setWarrantyLoaded(false);

      try {
        const warrantyRes = await ApiService.getWarrantyInfo(
          serialNoForApi,
          serialType,
        );
        if (cancelled) return;

        const itemList =
          warrantyRes?.status === 200
            ? warrantyRes.data?.warranty_item_list || []
            : [];
        const firstItem = itemList[0];
        const itemCode = firstItem?.main_item_code || "";
        setMainItemCode(itemCode);
        setCountryCode(firstItem?.country_code || "");
        setItemId1(itemList[0]?.item_id ?? null);
        setItemId2(itemList[1]?.item_id ?? null);
        setWarrantyLoaded(true);

        if (!itemCode) return;

        const productRes = await ApiService.getProductInfo(itemCode);
        if (cancelled) return;
        if (productRes?.status === 200) {
          setProductName(productRes.data?.product_name || "");
        }
      } catch (error) {
        console.error("Error fetching warranty / product info:", error);
        if (!cancelled) setWarrantyLoaded(true);
      }
    };

    fetchWarrantyData();
    return () => {
      cancelled = true;
    };
  }, [serialNoForApi, serialType]);

  const trans = useMemo(
    () =>
      applyProductName(
        LANGUAGES[lang] || en,
        localizeProductName(productName, lang),
      ),
    [lang, productName],
  );

  return (
    <LanguageContext.Provider
      value={{
        trans,
        lang,
        setLanguage,
        mainItemCode,
        countryCode,
        itemId1,
        itemId2,
        warrantyLoaded,
      }}
    >
      {children}
    </LanguageContext.Provider>
  );
};

export const useLanguage = () => useContext(LanguageContext);
